import { Mic, User, FileText, CalendarRange, Banknote, Check, Hand, ScanLine, Nfc, QrCode } from "lucide-react";

export function PhoneFrame({ children, className = "" }) {
  return (
    <div className={`relative mx-auto w-[280px] rounded-[2.6rem] border-[10px] border-[#0B1222] bg-white shadow-[0_40px_80px_-20px_rgba(15,23,42,0.55)] overflow-hidden ${className}`}>
      <div className="absolute top-0 left-1/2 -translate-x-1/2 w-24 h-5 bg-[#0B1222] rounded-b-2xl z-10" />
      <div className="pt-7 pb-3 px-3 min-h-[520px] bg-slate-50 text-slate-900">{children}</div>
    </div>
  );
}

export function DictationMock() {
  const objs = [
    { icon: User, label: "Client", val: "Sophie Martin · Lyon 3e" },
    { icon: FileText, label: "Devis", val: "Salle de bain · 4 500 €" },
    { icon: CalendarRange, label: "Planning", val: "15 → 17 juin" },
    { icon: Banknote, label: "Échéancier", val: "Acompte 1 350 € · Solde 3 150 €" },
  ];
  return (
    <PhoneFrame>
      <div className="rounded-2xl bg-[#0F172A] text-white p-3">
        <div className="flex items-center gap-2 text-[11px] font-semibold"><span className="w-2 h-2 rounded-full bg-rose-500 animate-pulse" /> Dictée en cours…</div>
        <div className="flex items-end gap-[3px] h-10 mt-2">
          {Array.from({ length: 26 }).map((_, i) => (
            <span key={i} className="flex-1 rounded-full bg-[#10B981] produit-wave" style={{ animationDelay: `${(i % 9) * 0.09}s` }} />
          ))}
        </div>
        <p className="text-[10px] text-slate-300 mt-2 leading-snug italic">« Salle de bain chez Mme Martin, 4 500 €, signé, du 15 au 17 juin, acompte 30 %… »</p>
      </div>
      <p className="text-[10px] font-bold uppercase tracking-widest text-slate-500 mt-4 mb-2">L'IA a créé</p>
      <div className="space-y-2">
        {objs.map((o, i) => {
          const Icon = o.icon;
          return (
            <div key={o.label} className="produit-rise flex items-center gap-2.5 bg-white rounded-xl p-2.5 border border-slate-200" style={{ animationDelay: `${0.4 + i * 0.25}s` }}>
              <div className="w-8 h-8 rounded-lg bg-emerald-50 flex items-center justify-center"><Icon className="w-4 h-4 text-[#10B981]" /></div>
              <div className="min-w-0"><div className="text-[9px] uppercase tracking-wide text-slate-400 font-bold">{o.label}</div><div className="text-[11px] font-semibold truncate">{o.val}</div></div>
              <Check className="w-3.5 h-3.5 text-[#10B981] ml-auto shrink-0" />
            </div>
          );
        })}
      </div>
      <div className="mt-3 h-9 rounded-xl bg-[#10B981] text-white text-[11px] font-bold flex items-center justify-center gap-1.5"><Check className="w-3.5 h-3.5" /> Valider et tout créer</div>
      <div className="mt-4 flex justify-center"><div className="w-12 h-12 rounded-full bg-[#0F172A] text-white flex items-center justify-center shadow-lg ring-4 ring-white"><Mic className="w-5 h-5" /></div></div>
    </PhoneFrame>
  );
}

export function PlanningMock() {
  const days = ["L", "M", "M", "J", "V", "S", "D"];
  const bands = [
    { row: 0, from: 0, to: 2, label: "SdB · Martin", cls: "bg-indigo-500" },
    { row: 1, from: 3, to: 4, label: "Peinture · Durand", cls: "bg-amber-500" },
    { row: 2, from: 1, to: 1, label: "Devis Leroy", cls: "bg-sky-500" },
    { row: 2, from: 4, to: 6, label: "Terrasse · Petit", cls: "bg-[#10B981]" },
  ];
  return (
    <div className="rounded-3xl bg-white border border-slate-200 p-5 shadow-xl shadow-slate-900/5" data-testid="produit-mock-planning">
      <div className="flex justify-between text-xs font-bold text-slate-500 mb-1"><span>Juin 2026</span><span>Semaine 25</span></div>
      <div className="grid grid-cols-7 text-center text-[11px] font-semibold text-slate-400 mb-2">{days.map((d, i) => <div key={i}>{d}<div className="text-slate-800 text-sm">{15 + i}</div></div>)}</div>
      <div className="relative h-[120px]">
        {bands.map((b, i) => (
          <div key={i} className={`produit-band absolute h-8 rounded-lg ${b.cls} text-white text-[11px] font-semibold px-2 flex items-center truncate shadow-sm`}
            style={{ top: b.row * 40, left: `calc(${(b.from / 7) * 100}% + 2px)`, width: `calc(${((b.to - b.from + 1) / 7) * 100}% - 4px)`, animationDelay: `${i * 0.15}s` }}>{b.label}</div>
        ))}
      </div>
    </div>
  );
}

export function KanbanMock() {
  const cols = [
    { t: "Envoyé", dot: "bg-sky-500", items: [["Cuisine · Roux", "8 200 €"]] },
    { t: "Signé", dot: "bg-indigo-500", items: [["SdB · Martin", "4 500 €"], ["Clôture · Petit", "2 100 €"]] },
    { t: "Encaissé", dot: "bg-[#10B981]", items: [["Peinture · Durand", "1 200 €"]] },
  ];
  return (
    <div className="grid grid-cols-3 gap-3" data-testid="produit-mock-kanban">
      {cols.map((c, ci) => (
        <div key={c.t} className="rounded-2xl bg-slate-100 p-2 space-y-2">
          <div className="text-[11px] font-bold flex items-center gap-1.5 px-1"><span className={`w-2 h-2 rounded-full ${c.dot}`} />{c.t}</div>
          {c.items.map(([n, v], i) => (
            <div key={n} className={`relative rounded-xl overflow-hidden ${ci === 1 && i === 0 ? "produit-swipe-wrap" : ""}`}>
              {ci === 1 && i === 0 && <div className="absolute inset-0 bg-[#10B981] text-white text-[10px] font-bold flex items-center pl-2 gap-1"><Banknote className="w-3 h-3" /> Encaisser</div>}
              <div className={`relative bg-white border border-slate-200 rounded-xl p-2 ${ci === 1 && i === 0 ? "produit-swipe" : ""}`}>
                <div className="text-[11px] font-semibold truncate">{n}</div>
                <div className={`text-xs font-extrabold tabular-nums mt-0.5 ${ci === 2 ? "text-[#10B981]" : "text-[#0F172A]"}`}>{v}</div>
              </div>
            </div>
          ))}
        </div>
      ))}
      <div className="col-span-3 text-[11px] text-slate-500 flex items-center gap-1.5"><Hand className="w-3.5 h-3.5" /> Glissez → encaissé · ← terminé ou note</div>
    </div>
  );
}

export function TreasuryMock() {
  const bars = [38, 52, 44, 61, 70, 58, 82, 76, 64, 90, 0, 0];
  const fc = [0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 72, 55];
  return (
    <div className="rounded-3xl bg-[#0F172A] text-white p-5 shadow-2xl shadow-slate-900/30" data-testid="produit-mock-treasury">
      <div className="grid grid-cols-2 gap-3">
        <div className="rounded-2xl bg-white/5 border border-white/10 p-3">
          <div className="text-[10px] uppercase tracking-widest text-slate-400 font-bold">CA encaissé</div>
          <div className="font-heading text-2xl font-extrabold text-[#10B981] tabular-nums mt-1">18 450 €</div>
          <div className="text-[10px] text-slate-400">dates de paiement réelles</div>
        </div>
        <div className="rounded-2xl bg-white/5 border border-white/10 p-3">
          <div className="text-[10px] uppercase tracking-widest text-slate-400 font-bold">CA prévisionnel</div>
          <div className="font-heading text-2xl font-extrabold tabular-nums mt-1">9 650 €</div>
          <div className="text-[10px] text-orange-400">2 échéances en retard</div>
        </div>
      </div>
      <div className="flex items-end gap-1.5 h-28 mt-5">
        {bars.map((b, i) => (
          <div key={i} className="flex-1 flex flex-col justify-end h-full">
            <div className={`produit-bar rounded-t-md ${b ? "bg-[#10B981]" : "bg-white/20 border border-dashed border-white/40"}`} style={{ height: `${b || fc[i]}%`, animationDelay: `${i * 0.06}s` }} />
          </div>
        ))}
      </div>
      <div className="flex justify-between text-[9px] text-slate-500 mt-1.5"><span>Jan</span><span>Juin</span><span>Déc</span></div>
    </div>
  );
}

export function CaptureMock() {
  const items = [
    { icon: ScanLine, t: "Carte de visite", d: "OCR IA en 3 s" },
    { icon: Nfc, t: "Carte NFC Kallitag", d: "Un tap, la fiche est prête" },
    { icon: QrCode, t: "QR inversé", d: "Le prospect saisit lui-même" },
  ];
  return (
    <div className="space-y-3" data-testid="produit-mock-capture">
      {items.map((it, i) => {
        const Icon = it.icon;
        return (
          <div key={it.t} className="produit-rise flex items-center gap-4 rounded-2xl bg-white border border-slate-200 p-4 shadow-sm" style={{ animationDelay: `${i * 0.15}s` }}>
            <div className="w-11 h-11 rounded-xl bg-[#0F172A] flex items-center justify-center"><Icon className="w-5 h-5 text-[#10B981]" /></div>
            <div><div className="font-semibold text-slate-900">{it.t}</div><div className="text-sm text-slate-500">{it.d}</div></div>
          </div>
        );
      })}
    </div>
  );
}
